import type {
  RouteDeviationCheckable,
  RouteDeviationVariant,
} from '@/apis/types';
import { isThrottled, refusalText } from './routeDeviationErrors';

export const SETUP_TITLE = 'Set-up route deviation alert';
export const SUGGESTED_ROUTES_TITLE = 'Select suggested routes';
export const MOST_USED_ROUTE = 'Most used route';
export const ALTERNATIVE_ROUTE = 'Alternative route';
export const LOADING_ROUTES =
  'Finding routes from past deliveries… this can take a few seconds';
export const LOAD_FAILED = "Couldn't read past trips right now.";
export const RETRY = 'Retry';
export const NO_PAST_TRIPS =
  'No past deliveries on this lane yet, so there are no routes to choose.';
/** The check needs 2 readings in a row off route, so an hourly sensor takes 1 to 2 hours. */
export const SLOW_SENSOR =
  'This sensor reports every hour or less often, so an alert can take 1–2 hours or more and short detours can be missed.';
export const TOO_MANY_TRIES = 'Too many tries, wait a moment.';
export const PICK_A_ROUTE = 'Pick at least one route';
export const MILES_INVALID = 'Enter 1 to 10 miles, with at most one decimal.';
export const SAVED = 'Route deviation saved';
export const SAVE_FAILED = "Couldn't save the route deviation. Try again.";
export const REMOVED = 'Route deviation removed';
export const REMOVE_FAILED = "Couldn't remove the route deviation. Try again.";
export const REMOVE_CONFIRM = 'Remove this route deviation alert?';
export const LANE_CHANGED =
  "Routes reset because this shipment's pickup or delivery changed. Choose again.";

/** Why a lane cannot be checked, in the server's words (the search answers with a code only). */
export const UNCHECKABLE_MESSAGES: Partial<
  Record<RouteDeviationCheckable, string>
> = {
  no_coordinates:
    "This shipment's pickup or delivery has no coordinates, so its route cannot be checked.",
  not_road:
    'This shipment flies or rides rail for part of the way, so there is no road route to check.',
  too_short: 'This lane is too short to have a route to check.',
};

export const builtFrom = (deliveries: number): string =>
  `Built from ${deliveries} prior ${
    deliveries === 1 ? 'delivery' : 'deliveries'
  } on this lane`;

/** `Sep 30` for this year, `Feb 18, 2025` for an earlier one. */
export const formatLastDelivered = (
  iso: string,
  now: Date = new Date()
): string => {
  const date = new Date(iso);
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

/** `602 mi · 19 of 23 deliveries · last Sep 30` */
export const describeVariant = (
  variant: RouteDeviationVariant,
  totalDeliveries: number
): string =>
  [
    `${Math.round(variant.length_mi).toLocaleString('en-US')} mi`,
    `${variant.trip_count} of ${totalDeliveries} deliveries`,
    `last ${formatLastDelivered(variant.last_delivered_at)}`,
  ].join(' · ');

/** The city in `1221 Jones Street, San Francisco, CA` or `Nashville, TN`. */
export const cityOf = (address?: string): string | undefined => {
  const parts = (address ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) return undefined;
  return parts.length === 2 ? parts[0] : parts[parts.length - 2];
};

/** `3 miles`, `1 mile` */
const milesLabel = (miles: number): string =>
  `${miles} ${miles === 1 ? 'mile' : 'miles'}`;

/** `Alerts when the shipment stays more than 3 miles off route for 10 minutes.` */
export const toleranceHelper = (miles: number, minutes: number): string =>
  minutes === 0
    ? `Alerts as soon as the shipment is more than ${milesLabel(miles)} off route.`
    : `Alerts when the shipment stays more than ${milesLabel(miles)} off route for ${minutes} minutes.`;

/** The message for a refused call: the rate limit's own wording, else the server's, else the fallback. */
export const refusalMessage = (response: unknown, fallback: string): string =>
  isThrottled(response) ? TOO_MANY_TRIES : (refusalText(response) ?? fallback);
